/**
 * Theme choice logic (CODING_STANDARDS §2). Pure so it can be unit-tested; the DOM wiring lives in
 * `components/theme.tsx`.
 *
 * The inline pre-paint script in `apps/web/index.html` mirrors this (same key, fallback and colours);
 * `theme-script.test.ts` runs that script against this module so the two can't drift.
 */
export type ThemeChoice = "light" | "dark" | "system";
export type EffectiveTheme = "light" | "dark";

export function effectiveTheme(choice: ThemeChoice, systemPrefersDark: boolean): EffectiveTheme {
  if (choice === "system") return systemPrefersDark ? "dark" : "light";
  return choice;
}

export const THEME_STORAGE_KEY = "kopi-theme";
export const THEME_CHOICES: readonly ThemeChoice[] = ["light", "dark", "system"];

/** The slice of `Storage` used here, so tests can pass a stand-in. */
export type ThemeStorage = Pick<Storage, "getItem" | "setItem">;

export function isThemeChoice(value: unknown): value is ThemeChoice {
  return typeof value === "string" && (THEME_CHOICES as readonly string[]).includes(value);
}

/** The stored choice, or `system` when nothing valid is stored or storage is unavailable. */
export function readThemeChoice(storage: ThemeStorage | undefined): ThemeChoice {
  try {
    const value = storage?.getItem(THEME_STORAGE_KEY);
    return isThemeChoice(value) ? value : "system";
  } catch {
    // Private mode / blocked storage: fall back silently.
    return "system";
  }
}

/** Persists the choice; a failing storage only loses persistence, never the in-page theme. */
export function writeThemeChoice(storage: ThemeStorage | undefined, choice: ThemeChoice): void {
  try {
    storage?.setItem(THEME_STORAGE_KEY, choice);
  } catch {
    // Ignore: the theme still applies for this page load.
  }
}

/** `<meta name="theme-color">` per effective theme (Paper and Roast, the `--background` tokens), for the OS status bar. */
export const THEME_COLOR: Record<EffectiveTheme, string> = { light: "#f7f3ec", dark: "#14211c" };
